import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  CheckCircle2,
  XCircle,
  ChevronRight,
  TrendingUp,
  Database,
  Loader2,
  RefreshCw,
  Zap,
  Layers,
} from "lucide-react";
import { mapTestSummary } from "@/lib/testFormat";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CandidateDetailSheet,
  CandidateTableRow,
} from "@/components/dashboard/CandidateDetailSheet";
import {
  seedMockData as seedMockDataAPI,
  evaluateTest as evaluateTestAPI,
  getTestResults,
  apiError,
} from "@/api";

export default function DashboardPage() {
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [evaluating, setEvaluating] = useState(null);
  const [dataSource, setDataSource] = useState("loading"); // "loading" | "live" | "error"
  const [message, setMessage] = useState(null);

  const graded = candidates.filter((c) => !c.isReference);
  const passCount = graded.filter((c) => c.result === "PASS").length;
  const failCount = graded.filter((c) => c.result === "FAIL").length;
  const refCount = candidates.length - graded.length;
  const scored = graded.filter((c) => c.result === "PASS" || c.result === "FAIL");
  const avgScore = scored.length ? Math.round(scored.reduce((s, c) => s + c.overallScore, 0) / scored.length) : 0;

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTestResults();
      const mapped = (data.results || []).map(mapTestSummary);
      setCandidates(mapped);
      setDataSource("live");
      setSelectedCandidate((prev) => (prev ? mapped.find((c) => c.id === prev.id) || prev : prev));
    } catch (e) {
      setDataSource("error");
      setMessage({ type: "error", text: `Could not load tests from Firebase: ${apiError(e)}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleSeedMock = useCallback(async () => {
    setSeeding(true);
    setMessage(null);
    try {
      const data = await seedMockDataAPI("all");
      setMessage({ type: "success", text: `Seeded ${data.testIds?.length || 0} demo tests to Firebase` });
      await fetchResults();
    } catch (e) {
      setMessage({ type: "error", text: `Seed failed: ${e.response?.data?.error || e.message}` });
    }
    setSeeding(false);
  }, [fetchResults]);

  const handleEvaluate = useCallback(async (testId, refresh = true) => {
    setEvaluating(testId);
    setMessage(null);
    try {
      const data = await evaluateTestAPI(testId);
      const text = data.isReference
        ? `${testId}: reference metrics saved`
        : `${testId}: ${data.result} (Score: ${data.overallScore})`;
      setMessage({ type: "success", text });
      if (refresh) await fetchResults();
    } catch (e) {
      setMessage({ type: "error", text: `Evaluate failed: ${apiError(e)}` });
    }
    setEvaluating(null);
  }, [fetchResults]);

  const openDetail = useCallback((candidate) => {
    setSelectedCandidate(candidate);
    setSheetOpen(true);
  }, []);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 mb-2">
          <Link to="/" className="hover:text-yellow-500 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-yellow-500">Dashboard</span>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 data-testid="dashboard-heading" className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-white">
              Test Dashboard
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Admin overview of all candidate test results and sensor data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono px-2 py-1 rounded ${dataSource === "live" ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>
              {dataSource === "live" ? "FIREBASE LIVE" : dataSource === "error" ? "OFFLINE" : "LOADING"}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            data-testid="seed-mock-btn"
            onClick={handleSeedMock}
            disabled={seeding}
            className="inline-flex items-center gap-2 bg-zinc-800 text-white border border-white/10 py-2 px-4 rounded-lg hover:bg-zinc-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {seeding ? "Seeding..." : "Seed Demo Tests (001-010)"}
          </button>
          <button
            data-testid="fetch-results-btn"
            onClick={fetchResults}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-zinc-800 text-white border border-white/10 py-2 px-4 rounded-lg hover:bg-zinc-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {loading ? "Loading..." : "Refresh"}
          </button>
          {graded.some((c) => c.result === "PENDING" && c.sampleCount > 0) && (
            <button
              data-testid="evaluate-all-btn"
              onClick={async () => {
                const pending = graded.filter((c) => c.result === "PENDING" && c.sampleCount > 0);
                for (const c of pending) {
                  await handleEvaluate(c.id, false);
                }
                await fetchResults();
              }}
              className="inline-flex items-center gap-2 bg-yellow-500 text-black py-2 px-4 rounded-lg hover:bg-yellow-400 transition-colors text-sm font-bold"
            >
              <Zap className="w-4 h-4" />
              Evaluate All Pending
            </button>
          )}
        </div>

        {/* Status Message */}
        {message && (
          <div className={`mb-6 px-4 py-3 rounded-lg text-sm font-mono ${message.type === "success" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
            {message.text}
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Total Tests" value={candidates.length} color="text-yellow-500" testId="stat-total" />
          <StatCard icon={CheckCircle2} label="Passed" value={passCount} color="text-emerald-400" testId="stat-passed" />
          <StatCard icon={XCircle} label="Failed" value={failCount} color="text-red-400" testId="stat-failed" />
          <StatCard icon={TrendingUp} label="Avg Score" value={avgScore} color="text-sky-400" testId="stat-avg" />
          <StatCard icon={Layers} label="Reference" value={refCount} color="text-sky-300" testId="stat-reference" />
        </div>

        {/* Candidates Table */}
        <div data-testid="candidates-table" className="bg-zinc-900 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400">
              Candidate Results
            </span>
          </div>
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-zinc-400">Loading from Firebase...</p>
            </div>
          ) : !candidates.length ? (
            <div data-testid="dashboard-empty" className="p-12 text-center text-sm text-zinc-400">No tests in Firebase yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Candidate</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider">ID</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider hidden sm:table-cell">Vehicle</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider hidden md:table-cell">Recorded</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Score</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-zinc-400 font-mono text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <tbody className="[&_tr:last-child]:border-0">
                {candidates.map((candidate) => (
                  <CandidateTableRow
                    key={candidate.id}
                    candidate={candidate}
                    onView={openDetail}
                    evaluating={evaluating === candidate.id}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <CandidateDetailSheet
          candidate={selectedCandidate}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onEvaluate={handleEvaluate}
          evaluating={evaluating}
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900 border border-white/10 p-5 rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400">
          {label}
        </span>
      </div>
      <span className="text-3xl font-mono font-medium text-white">{value}</span>
    </div>
  );
}
